// src/components/MaintenanceToggle.js — extracted from App.js
import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabase";

// ─── Maintenance Toggle ───────────────────────────────────────────────────────
export function MaintenanceToggle({ maintenance, toggleMaintenance }) {
  const [msg, setMsg] = useState(maintenance?.message || "");
  const isOn = maintenance?.on;
  return (
    <div style={{border:`1.5px solid ${isOn?"var(--rbd)":"var(--bd)"}`,borderRadius:12,padding:"16px 20px",marginBottom:20,display:"flex",gap:16,alignItems:"flex-start",background:isOn?"var(--rbg)":"#fff"}}>
      <div style={{flex:1}}>
        <div style={{fontWeight:600,fontSize:15,marginBottom:4,color:isOn?"var(--red)":"var(--ink)"}}>🔧 Maintenance Mode {isOn&&<span style={{fontSize:10,fontFamily:"'DM Mono',monospace",letterSpacing:"0.1em",background:"var(--red)",color:"#fff",borderRadius:4,padding:"1px 6px",marginLeft:6}}>ACTIVE</span>}</div>
        <div style={{fontSize:12,color:"var(--ink3)",lineHeight:1.6,marginBottom:10}}>When on, all visitors (except admins) see a maintenance screen. You keep full access.</div>
        <textarea value={msg} onChange={e=>setMsg(e.target.value)} placeholder="Message to show visitors…" rows={2} style={{fontSize:12,resize:"vertical",borderRadius:8,border:"1px solid var(--bd)",padding:"8px 10px",width:"100%",boxSizing:"border-box"}}/>
      </div>
      <button onClick={()=>toggleMaintenance(!isOn, msg||"We're making improvements. Back shortly.")} style={{padding:"10px 18px",borderRadius:50,cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:10,letterSpacing:"0.08em",flexShrink:0,background:isOn?"var(--red)":"#111118",color:"#fff",border:"none",marginTop:4}}>
        {isOn?"✗ End maintenance":"⚙ Start maintenance"}
      </button>
    </div>
  );
}


// ─── AI Dashboard ─────────────────────────────────────────────────────────────
export function AiDashboard({ aiEnabled, toggleAi, maintenance, toggleMaintenance, tickerEnabled=true, toggleTicker, aiBannerEnabled=true, toggleAiBanner, navStates={}, setNavState }) {
  const [logs, setLogs]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod]   = useState("30"); // days

  useEffect(() => {
    (async () => {
      setLoading(true);
      const since = new Date(Date.now() - parseInt(period)*24*60*60*1000).toISOString();
      const { data } = await supabase.from("ai_usage_logs").select("*").gte("created_at", since).order("created_at", { ascending:false });
      setLogs(data || []);
      setLoading(false);
    })();
  }, [period]);

  const totalCost  = logs.reduce((s,l)=>s+parseFloat(l.cost_usd||0), 0);
  const totalIn    = logs.reduce((s,l)=>s+(l.input_tokens||0), 0);
  const totalOut   = logs.reduce((s,l)=>s+(l.output_tokens||0), 0);

  const byFn = logs.reduce((m,l)=>{ m[l.function_name]=(m[l.function_name]||{calls:0,cost:0}); m[l.function_name].calls++; m[l.function_name].cost+=parseFloat(l.cost_usd||0); return m; },{});
  const byModel = logs.reduce((m,l)=>{ m[l.model]=(m[l.model]||{calls:0,cost:0}); m[l.model].calls++; m[l.model].cost+=parseFloat(l.cost_usd||0); return m; },{});

  const fmt = (n) => n < 0.001 ? "<$0.001" : "$"+n.toFixed(4);

  const Toggle = ({ on, onToggle, label, desc, color="var(--grn)" }) => (
    <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:16,padding:"14px 18px",background:"#fff",borderRadius:10,border:"1.5px solid var(--bd)",marginBottom:10}}>
      <div>
        <div style={{fontWeight:500,fontSize:13,marginBottom:2}}>{label}</div>
        <div style={{fontSize:11,color:"var(--ink3)",lineHeight:1.5}}>{desc}</div>
      </div>
      <button onClick={()=>onToggle(!on)} style={{flexShrink:0,width:48,height:28,borderRadius:14,border:"none",cursor:"pointer",background:on?color:"#D1D5DB",position:"relative",transition:"background .22s",padding:0}}>
        <span style={{position:"absolute",top:3,left:on?23:3,width:22,height:22,borderRadius:"50%",background:"#fff",transition:"left .22s",display:"block",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
      </button>
    </div>
  );

  return (
    <div>
      {/* Platform Controls */}
      <div style={{marginBottom:24}}>
        <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",letterSpacing:"0.12em",marginBottom:12}}>PLATFORM CONTROLS</div>
        <Toggle on={tickerEnabled} onToggle={toggleTicker} label="Running ticker" desc="The scrolling marquee bar at the top of every page (HMT · ALLWYN · RICOH…)." color="var(--accent)"/>
        <Toggle on={!maintenance?.on} onToggle={(v)=>toggleMaintenance(!v, maintenance?.message||"We're making improvements. Back shortly.")} label="Site open to visitors" desc={maintenance?.on ? `Maintenance active — showing: "${maintenance?.message}"` : "All visitors can access the platform normally."} color="var(--accent)"/>
        <Toggle on={aiEnabled} onToggle={toggleAi} label="AI features" desc="Photo detection, ID validation, smart search. Disable to cut API costs; keyword fallbacks are used." color="var(--accent)"/>
        <Toggle on={aiBannerEnabled} onToggle={toggleAiBanner} label="Show AI-disabled warning banner" desc='The amber banner shown to users when AI is off. Hide it if you want a silent fallback.' color="var(--accent)"/>
      </div>

      {/* Feature Visibility — 3 states per nav section */}
      <div style={{marginBottom:24}}>
        <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",letterSpacing:"0.12em",marginBottom:4}}>FEATURE VISIBILITY</div>
        <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginBottom:12}}>Hidden = removed from nav · Soon = teaser screen · New = live with a "NEW" badge</div>
        {[
          ["auth-svc",  "Authenticate a Watch"],
          ["services",  "Services"],
          ["wotd",      "Watch of the Day"],
          ["stories",   "HMT Stories"],
          ["labs",      "Watch Labs"],
          ["universe",  "Watch Universe"],
        ].map(([key, label]) => {
          const cur = navStates[key] || "live";
          const opts = [["hidden","Hidden","#6B7280"],["coming_soon","Soon","#7C3AED"],["live","Live","var(--accent)"],["new","New","var(--gold)"]];
          return (
            <div key={key} style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:12,padding:"11px 18px",background:"#fff",borderRadius:10,border:`1.5px solid ${cur==="live"?"var(--bd)":cur==="new"?"#D97706":cur==="coming_soon"?"#7C3AED":"#9CA3AF"}`,marginBottom:8}}>
              <div style={{fontWeight:500,fontSize:13}}>{label}</div>
              <div style={{display:"flex",gap:4}}>
                {opts.map(([val, lbl, col])=>(
                  <button key={val} onClick={()=>setNavState?.(key, val==="live"?null:val)}
                    style={{padding:"4px 10px",borderRadius:6,border:"none",cursor:"pointer",fontFamily:"'DM Mono',monospace",fontSize:9,letterSpacing:"0.06em",
                      background:cur===val||(!navStates[key]&&val==="live")?col:"var(--bg2)",
                      color:cur===val||(!navStates[key]&&val==="live")?"#fff":"var(--ink3)",
                      transition:"background .18s"}}>
                    {lbl}
                  </button>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Maintenance message editor */}
      <MaintenanceToggle maintenance={maintenance} toggleMaintenance={toggleMaintenance}/>

      {/* Period selector */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
        <div style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--ink3)",letterSpacing:"0.1em"}}>USAGE & COST</div>
        <div style={{display:"flex",gap:4}}>
          {[["7","7d"],["30","30d"],["90","90d"]].map(([v,l])=>(
            <button key={v} onClick={()=>setPeriod(v)} style={{padding:"4px 10px",fontSize:9,fontFamily:"'DM Mono',monospace",borderRadius:6,border:"1px solid var(--bd)",background:period===v?"#111118":"transparent",color:period===v?"#fff":"var(--ink3)",cursor:"pointer"}}>{l}</button>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:20}}>
        {[["Total Cost",fmt(totalCost),"var(--gold)"],["API Calls",logs.length,"var(--ink)"],["Input Tokens",totalIn.toLocaleString(),"var(--blu)"],["Output Tokens",totalOut.toLocaleString(),"var(--grn)"]].map(([k,v,c])=>(
          <div key={k} className="card" style={{textAlign:"center",padding:"16px 10px"}}>
            <div style={{fontFamily:"'Playfair Display',serif",fontSize:22,color:c,marginBottom:4}}>{loading?"…":v}</div>
            <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",textTransform:"uppercase"}}>{k}</div>
          </div>
        ))}
      </div>

      {/* By function */}
      {Object.keys(byFn).length>0 && (
        <div style={{marginBottom:20}}>
          <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:10}}>BY FEATURE</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {Object.entries(byFn).sort((a,b)=>b[1].cost-a[1].cost).map(([fn,s])=>(
              <div key={fn} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"var(--bg2)",borderRadius:8,padding:"10px 14px"}}>
                <div>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:11,fontWeight:500}}>{fn}</div>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",marginTop:2}}>{s.calls} call{s.calls!==1?"s":""}</div>
                </div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:16,color:"var(--gold)"}}>{fmt(s.cost)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* By model */}
      {Object.keys(byModel).length>0 && (
        <div style={{marginBottom:20}}>
          <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:10}}>BY MODEL</div>
          <div style={{display:"flex",flexDirection:"column",gap:6}}>
            {Object.entries(byModel).sort((a,b)=>b[1].cost-a[1].cost).map(([model,s])=>(
              <div key={model} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"var(--bg2)",borderRadius:8,padding:"10px 14px"}}>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:10}}>{model}</div>
                <div style={{textAlign:"right"}}>
                  <div style={{fontFamily:"'Playfair Display',serif",fontSize:15,color:"var(--gold)"}}>{fmt(s.cost)}</div>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)"}}>{s.calls} calls</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent log */}
      {logs.length>0 && (
        <div>
          <div style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)",letterSpacing:"0.1em",marginBottom:10}}>RECENT CALLS</div>
          <div style={{display:"flex",flexDirection:"column",gap:4}}>
            {logs.slice(0,20).map(l=>(
              <div key={l.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",borderRadius:8,background:"var(--bg2)",fontSize:11}}>
                <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
                  <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)"}}>{l.created_at?.slice(0,16).replace("T"," ")}</span>
                  <span style={{fontFamily:"'DM Mono',monospace",fontSize:10}}>{l.function_name}</span>
                  <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)"}}>{l.model?.split("-").slice(0,2).join("-")}</span>
                </div>
                <div style={{display:"flex",gap:10,alignItems:"center",flexShrink:0}}>
                  <span style={{fontFamily:"'DM Mono',monospace",fontSize:9,color:"var(--ink3)"}}>{(l.input_tokens+l.output_tokens).toLocaleString()} tok</span>
                  <span style={{fontFamily:"'DM Mono',monospace",fontSize:10,color:"var(--gold)",minWidth:52,textAlign:"right"}}>{fmt(parseFloat(l.cost_usd||0))}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!loading && logs.length===0 && (
        <div style={{textAlign:"center",padding:48,fontFamily:"'DM Mono',monospace",fontSize:12,color:"var(--ink3)"}}>No AI calls recorded in this period.</div>
      )}
    </div>
  );
}

// ─── Beta Approve Panel ───────────────────────────────────────────────────────
